import { useEffect, useState } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LS_KEY = "whats_new_last_seen_release_id";

/**
 * WhatsNewPopover
 *
 * Shows a one-shot dialog with recent user-visible releases the first time a
 * signed-in user visits the Dashboard after we ship something. Dismiss is
 * remembered per browser (localStorage) AND per account (backend). The two
 * layers together mean returning users on a new device still see it once.
 */
export default function WhatsNewPopover() {
  const [open, setOpen] = useState(false);
  const [releases, setReleases] = useState([]);
  const [latestId, setLatestId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        // Don't compete with the first-run onboarding tour.
        if (document.querySelector('[data-testid="onboarding-scrim"]')) {
          return;
        }
        const token = localStorage.getItem("token");
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const res = await axios.get(`${API}/whats-new`, { headers });
        if (cancelled) return;
        const list = res.data?.releases || [];
        const latest = res.data?.latest_id;
        const backendSeen = res.data?.last_seen_release_id;
        const localSeen = localStorage.getItem(LS_KEY);
        const alreadySeen = latest && (backendSeen === latest || localSeen === latest);
        if (list.length > 0 && !alreadySeen) {
          setReleases(list.slice(0, 3));
          setLatestId(latest);
          // Slight delay so the tour has a chance to render its scrim first.
          setTimeout(() => {
            if (!document.querySelector('[data-testid="onboarding-scrim"]')) {
              setOpen(true);
            }
          }, 1200);
        }
      } catch (_err) {
        // silent — a failure here should never block the dashboard
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  const dismiss = async () => {
    if (latestId) localStorage.setItem(LS_KEY, latestId);
    setOpen(false);
    try {
      const token = localStorage.getItem("token");
      if (token) {
        await axios.post(`${API}/whats-new/dismiss`, {}, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }
    } catch (_err) { /* silent */ }
  };

  if (!open || releases.length === 0) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) dismiss(); }}>
      <DialogContent className="bg-slate-900 border-slate-800 max-w-lg" data-testid="whats-new-dialog">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-black" />
            </div>
            <DialogTitle className="text-xl">What&apos;s new in SlideCon</DialogTitle>
          </div>
          <DialogDescription className="text-slate-400">
            Here&apos;s what shipped while you were away.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2 max-h-[420px] overflow-y-auto">
          {releases.map((r) => (
            <div key={r.id} className="border border-slate-800 rounded-lg p-4 bg-slate-950/40" data-testid={`whats-new-item-${r.id}`}>
              <div className="text-xs uppercase tracking-wider text-amber-400/80 mb-1">{r.date}</div>
              <div className="font-semibold text-slate-100 mb-2">{r.title}</div>
              <ul className="text-sm text-slate-300 space-y-1">
                {(r.highlights || []).map((h, i) => (
                  <li key={i} className="leading-relaxed">{h}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button
            onClick={dismiss}
            className="bg-amber-500 text-black hover:bg-amber-400 w-full sm:w-auto"
            data-testid="whats-new-dismiss"
          >
            Got it, thanks
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
